/**
 * Receivable Offline Service
 *
 * Extends BaseOfflineService for accounts receivable operations.
 */

import type { Table } from 'dexie';

import { offlineDb } from '../database/offlineDatabase';
import { BaseOfflineService } from './BaseOfflineService';
import type {
  EntityType,
  OfflineEntity,
  ApiServiceMethods,
} from '../types/offline.types';

// Import receivable types
import type {
  AccountReceivable,
  CreateReceivableDto,
  UpdateReceivableDto,
  QueryReceivablesDto,
  ReceivablesSummary,
} from '@/types/receivables.types';
import { DebtStatus } from '@/types/enum';

// Import online service
import * as receivableService from '@/api/services/receivableService';

// ============================================
// OFFLINE SERVICE
// ============================================

export class ReceivableOfflineService extends BaseOfflineService<
  AccountReceivable,
  CreateReceivableDto,
  UpdateReceivableDto
> {
  protected readonly entityType: EntityType = 'receivable';
  protected readonly table: Table<OfflineEntity<AccountReceivable>, string> = offlineDb.receivables;

  protected readonly apiService: ApiServiceMethods<
    AccountReceivable,
    CreateReceivableDto,
    UpdateReceivableDto
  > = {
    getAll: (filters) => receivableService.getAll(filters as QueryReceivablesDto),
    getOne: receivableService.getOne,
    create: receivableService.create,
    update: receivableService.update,
    delete: receivableService.remove,
  };

  // ============================================
  // RECEIVABLE-SPECIFIC METHODS
  // ============================================

  /**
   * Get receivables by status
   */
  async getByStatus(status: DebtStatus): Promise<AccountReceivable[]> {
    const receivables = await this.table.where('status').equals(status).toArray();
    return receivables.map(this.stripOfflineMetadata);
  }

  /**
   * Get pending receivables (PENDING or PARTIAL)
   */
  async getPending(): Promise<AccountReceivable[]> {
    const receivables = await this.table
      .filter((r) => r.status === DebtStatus.ACTIVE || r.status === DebtStatus.PARTIAL)
      .toArray();
    return receivables.map(this.stripOfflineMetadata);
  }

  /**
   * Get receivables by contact
   */
  async getByContact(contactId: string): Promise<AccountReceivable[]> {
    const receivables = await this.table.where('contactId').equals(contactId).toArray();
    return receivables.map(this.stripOfflineMetadata);
  }

  /**
   * Get overdue receivables
   */
  async getOverdue(): Promise<AccountReceivable[]> {
    const today = new Date().toISOString().split('T')[0];
    const receivables = await this.table
      .filter((r) => {
        if (!r.dueDate) return false;
        return (
          r.dueDate < today &&
          (r.status === DebtStatus.ACTIVE || r.status === DebtStatus.PARTIAL)
        );
      })
      .toArray();
    return receivables.map(this.stripOfflineMetadata);
  }

  /**
   * Calculate summary from cached data
   */
  async getSummary(): Promise<ReceivablesSummary> {
    const receivables = await this.table.toArray();

    let totalOwed = 0;
    let pendingCount = 0;
    let paidCount = 0;
    let totalCollected = 0;

    for (const receivable of receivables) {
      if (receivable.status === DebtStatus.ACTIVE || receivable.status === DebtStatus.PARTIAL) {
        totalOwed += receivable.amount - receivable.paidAmount;
        pendingCount++;
      } else if (receivable.status === DebtStatus.PAID) {
        paidCount++;
      }
      totalCollected += receivable.paidAmount;
    }

    return {
      totalOwed,
      pendingCount,
      paidCount,
      totalCollected,
    };
  }

  /**
   * Override applyFilters for receivable-specific filtering
   */
  protected override applyFilters(
    items: OfflineEntity<AccountReceivable>[],
    filters: Record<string, unknown>
  ): OfflineEntity<AccountReceivable>[] {
    return items.filter((item) => {
      // Status filter
      if (filters.status && item.status !== filters.status) {
        return false;
      }

      // Contact filter
      if (filters.contactId && item.contactId !== filters.contactId) {
        return false;
      }

      // Branch filter
      if (filters.branchId && item.branchId !== filters.branchId) {
        return false;
      }

      // Date range filter
      if (filters.startDate && item.dueDate) {
        const startDate = new Date(filters.startDate as string);
        if (new Date(item.dueDate) < startDate) {
          return false;
        }
      }
      if (filters.endDate && item.dueDate) {
        const endDate = new Date(filters.endDate as string);
        if (new Date(item.dueDate) > endDate) {
          return false;
        }
      }

      return true;
    });
  }
}

// ============================================
// SINGLETON INSTANCE
// ============================================

export const receivableOfflineService = new ReceivableOfflineService();
